import type { registerPlugin } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';

/**
 * iOS 原生聊天插件接口
 *
 * 提供以下功能：
 * 1. openChat - 打开原生聊天页面
 * 2. showIncomingCall - 显示来电邀请（iOS 原生弹窗）
 * 3. notifyChatClosed - 通知 H5 聊天页面已关闭（用于刷新消息中心）
 *
 * 注意：此插件仅在 iOS App 中可用，Web/Android 环境会抛出错误
 */
export interface ChatPlugin {
  /**
   * 打开原生聊天页面
   * @param options.chatId - 聊天会话ID
   * @param options.chatName - 对方名称
   * @param options.chatIcon - 对方头像URL
   * @param options.targetUserId - 对方用户ID（用于生成固定的通话房间ID）
   * @param options.freelancerId - 技师ID（用于检查是否有进行中的订单）
   * @param options.chatType - 聊天类型（1-私聊 2-群聊 3-客服）
   * @param options.apiBaseUrl - API基础URL
   * @param options.token - 用户Token
   * @param options.userId - 当前用户ID
   */
  openChat(options: {
    chatId: string;
    chatName?: string;
    chatIcon?: string;
    targetUserId?: string;
    freelancerId?: string;
    chatType?: string;
    apiBaseUrl?: string;
    token?: string;
    userId?: string;
    technicianType?: string;
  }): Promise<void>;

  /**
   * 显示来电邀请（iOS 原生弹窗）
   * @param options.inviteId - 邀请ID（用于防止重复显示）
   * @param options.roomId - RTC 房间ID
   * @param options.callerName - 发起人名称
   * @param options.callerAvatar - 发起人头像URL
   */
  showIncomingCall(options: {
    inviteId: string;
    roomId: string;
    callerName?: string;
    callerAvatar?: string;
  }): Promise<void>;

  /**
   * 关闭来电邀请界面（iOS 原生弹窗）
   * 当对方取消通话邀请时调用
   */
  dismissIncomingCall(): Promise<void>;
}

/**
 * iOS 原生聊天插件实现
 */
export const ChatPlugin: ChatPlugin = {
  openChat: async (options) => {
    const { ChatPlugin: NativeChatPlugin } = Capacitor.Plugins;
    if (NativeChatPlugin) {
      return await NativeChatPlugin.openChat(options);
    } else {
      console.warn('ChatPlugin not available in web environment');
      throw new Error('原生聊天仅在 App 中可用');
    }
  },

  showIncomingCall: async (options) => {
    const { ChatPlugin: NativeChatPlugin } = Capacitor.Plugins;
    if (NativeChatPlugin) {
      return await NativeChatPlugin.showIncomingCall(options);
    } else {
      console.warn('ChatPlugin not available in web environment');
      throw new Error('原生来电仅在 App 中可用');
    }
  },

  dismissIncomingCall: async () => {
    const { ChatPlugin: NativeChatPlugin } = Capacitor.Plugins;
    if (NativeChatPlugin) {
      return await NativeChatPlugin.dismissIncomingCall();
    } else {
      console.warn('ChatPlugin not available in web environment');
      throw new Error('原生来电仅在 App 中可用');
    }
  },

  /**
   * 通知 H5 聊天页面已关闭（供 iOS 原生调用）
   */
  notifyChatClosed: async () => {
    const { ChatPlugin: NativeChatPlugin } = Capacitor.Plugins;
    if (NativeChatPlugin) {
      return await NativeChatPlugin.notifyChatClosed();
    } else {
      // Web 环境直接执行刷新
      console.log('📡 Web 环境：直接刷新消息中心');
      if (typeof window.refreshMessageCenter === 'function') {
        window.refreshMessageCenter();
      }
    }
  }
};
